import React, { useState, useEffect } from 'react'

import { useAuth } from '../../../../../../hooks/auth'

import Section from '../../../../../../components/Section'
import { Structure } from '../../../../Org/Structure/types'
import { Contract } from '../../../types'

import { User } from '../../../../Org/Users/types'

import { ApproversAdd } from '../../../../Plans/Positions/Edit/types'

import HoverTooltip from '../../../../../../components/HoverTooltip'

type ApiResponse = {
    message: User[]
}

type Props = {
    theme: string
    contract: Contract
    contractGuid: string
    structShortName: string | null
    structShortGuid: string | null
    structList: Structure[]
    isUserRoleName: string
    onSupervisorChange: (
        guid: string,
        f_name: string,
        l_name: string,
        p_name: string
    ) => void
}

function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

function formatDateTime(input: string): string {
    const date = new Date(input)

    const day = date.getDate()
    const month = date.toLocaleString('ru-RU', { month: 'long' })
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${day} ${capitalize(month)} ${year} ${hours}:${minutes}`
}

const ContractCardUserInfo: React.FC<Props> = (props) => {
    const { user } = useAuth()

    const [changeSupervisorPopup, setChangeSupervisorPopup] = useState(false)
    const [selectedSupervisor, setSelectedSupervisor] = useState<string>('')
    const [supervisorChanged, setSupervisorChanged] = useState(false)

    const [originalUserList, setOriginalUserList] = useState<User[]>([]) // Сохраняем исходный список пользователей

    const [approversAdd, setApproversAdd] = useState<ApproversAdd[]>([])
    const [isApprovalsAdd, setApprovalAdd] = useState(false) // Состояние для анимации загрузки

    const [userList, setUserList] = useState<User[]>([])
    const [userFilteredList, setUserFilteredList] = useState<User[]>([])
    const [userFilter, setUserFilter] = useState<string[]>([])

    const getUserList = async (): Promise<User[]> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                '/api/backend/v1/organization/users_fio',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .then((response: ApiResponse) => response.message)
            .catch((e) => {
                console.error(e)
                return []
            })
    }

    const handleSave = () => {
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/contract/${props.contractGuid}`
        fetch(url, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(props.contract),
        })
            .then((response) => {
                if (response.ok) {
                    // success
                    return response.json()
                } else {
                    throw new Error('Неверный почтовый адрес или пароль')
                }
            })
            .catch((error) => undefined)
            .finally(() => undefined)
    }

    const getNameUser = (user_guid: string) => {
        const nameUser = userList.find((u) => u.guid === user_guid)
        return (
            `${nameUser?.last_name} ${nameUser?.first_name} ${nameUser?.patronymic}` ||
            '-'
        )
    }

    const getNameStruct = (struct_guid: string) => {
        const nameSctruct = props.structList.find((s) => s.guid === struct_guid)
        return nameSctruct?.struct_short_name || '-'
    }

    const handleChangeSupervisorPopup = () => {
        setChangeSupervisorPopup(!changeSupervisorPopup)
        setSelectedSupervisor(props.contract.supervisor_user_guid)
        setUserFilter([''])
    }

    const handleSupervisorChange = () => {
        // Обновляем ответственного
        if (selectedSupervisor != '') {
            const nameUser = userList.find((u) => u.guid === selectedSupervisor)
            props.onSupervisorChange(
                selectedSupervisor,
                nameUser?.first_name ? nameUser?.first_name : '',
                nameUser?.last_name ? nameUser?.last_name : '',
                nameUser?.patronymic ? nameUser?.patronymic : ''
            )
            setSupervisorChanged(true) // Устанавливаем флаг изменения ответственного
        }

        // Очищаем выбранные элементы и закрываем popup
        setSelectedSupervisor('')
        setChangeSupervisorPopup(false)
        setUserFilter([''])
    }

    useEffect(() => {
        // Вызываем если supervisor_user_guid изменился и нажата кнопка изменить
        if (supervisorChanged) {
            handleSave()
            setSupervisorChanged(false) // Сбрасываем флаг
        }
    }, [props.contract.supervisor_user_guid])

    const handleCheckboxChangeSupervisor = (user_guid: string) => {
        setSelectedSupervisor(user_guid)
    }

    const handleInputChange = (value: string) => {
        setUserFilter([value])
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const users = await getUserList()
                if (Array.isArray(users)) {
                    setOriginalUserList(users) // Сохраняем исходный список пользователей
                    setUserList(users)

                    // Начальная фильтрация userFilteredList
                    const approversGuids = new Set(
                        approversAdd.map((approver) => approver.user_guid)
                    )
                    const filteredUsers = users.filter(
                        (u) => !approversGuids.has(String(u.guid))
                    )
                    setUserFilteredList(filteredUsers)
                } else {
                    console.error('Нет данных:', users)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    useEffect(() => {
        const updateFilteredList = () => {
            // Получаем список уже добавленных пользователей
            const approversGuids = new Set(
                approversAdd.map((approver) => approver.user_guid)
            )

            // Фильтруем исходный список, исключая уже выбранных
            let filtered = originalUserList.filter(
                (u) => !approversGuids.has(String(u.guid))
            )

            // Если активен текстовый фильтр, дополнительно фильтруем по тексту
            if (userFilter.length > 0) {
                filtered = filtered.filter((u) =>
                    userFilter.some(
                        (filter) =>
                            u.last_name
                                ?.toLowerCase()
                                .includes(filter.toLowerCase()) ||
                            u.first_name
                                ?.toLowerCase()
                                .includes(filter.toLowerCase()) ||
                            u.patronymic
                                ?.toLowerCase()
                                .includes(filter.toLowerCase())
                    )
                )
            }

            // Обновляем состояние отфильтрованных пользователей
            setUserFilteredList(filtered)
        }

        updateFilteredList()
    }, [approversAdd, originalUserList, userFilter]) // Зависимости

    return (
        <Section title="Ответственные исполнители">
            <div className="flex flex-col w-full gap-5 mt-2">
                <div className="flex flex-row w-full gap-5">
                    <div className="flex flex-col w-1/2">
                        <div className="flex w-full items-center justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                            Создал
                        </div>
                        <div className="flex flex-col w-full gap-3 items-start justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                            {props.contract?.user.last_name +
                                ' ' +
                                props.contract?.user.first_name +
                                ' ' +
                                props.contract?.user.patronymic}
                        </div>
                    </div>
                    <div className="flex flex-col w-1/2">
                        <div className="flex w-full items-center justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                            Дата создания
                        </div>
                        <div className="flex flex-col w-full gap-3 items-start justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                            {props.contract?.sys_created_at != null
                                ? formatDateTime(props.contract.sys_created_at)
                                : ''}
                        </div>
                    </div>
                </div>
                <div className="flex flex-row w-full gap-2">
                    <div className="flex flex-col">
                        <div className="flex w-full items-center justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                            Ответственный
                        </div>
                        <div className="flex flex-col w-full gap-3 items-start justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                            {props.contract?.supervisor_user
                                ? props.contract?.supervisor_user.last_name +
                                  ' ' +
                                  props.contract?.supervisor_user.first_name +
                                  ' ' +
                                  props.contract?.supervisor_user.patronymic
                                : '-'}
                            {}
                        </div>
                    </div>
                    <div className="flex h-8 w-8 mt-4 items-center justify-center text-center">
                        {(props.contract.user_guid == user?.object.user_id ||
                            props.contract.supervisor_user_guid ==
                                user?.object.user_id ||
                            props.isUserRoleName == 'admin' ||
                            props.isUserRoleName == 'specialist' ||
                            props.isUserRoleName == 'curator') && (
                            <HoverTooltip tooltipText="Изменить">
                                <button
                                    onClick={handleChangeSupervisorPopup}
                                    className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="edit-alt"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M5,18H9.24a1,1,0,0,0,.71-.29l6.92-6.93h0L19.71,8a1,1,0,0,0,0-1.42L15.47,2.29a1,1,0,0,0-1.42,0L11.23,5.12h0L4.29,12.05a1,1,0,0,0-.29.71V17A1,1,0,0,0,5,18ZM14.76,4.41l2.83,2.83L16.17,8.66,13.34,5.83ZM6,13.17l5.93-5.93,2.83,2.83L8.83,16H6ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </button>
                            </HoverTooltip>
                        )}
                    </div>
                </div>
                {changeSupervisorPopup && (
                    <div
                        className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                        onClick={handleChangeSupervisorPopup}
                    >
                        <div
                            className="flex flex-col p-5 rounded-lg w-[800px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                Реестр пользователей
                            </h2>
                            <div className="flex flex-row w-full min-h-10 p-1 gap-1 rounded-md items-center justify-start flex-wrap bg-[#FFFFFF] dark:bg-[#181818]">
                                {selectedSupervisor == '' ||
                                selectedSupervisor == null ? (
                                    ''
                                ) : (
                                    <div className="flex flex-row w-fit h-8 p-1 pl-2 gap-2 rounded-md items-center justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#ebebeb] dark:bg-[#282629]">
                                        {getNameUser(selectedSupervisor)}
                                        <HoverTooltip tooltipText="Удалить пльзователя">
                                            <button
                                                onClick={() =>
                                                    handleCheckboxChangeSupervisor(
                                                        ''
                                                    )
                                                }
                                                className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                            >
                                                <svg
                                                    className="w-3 h-3"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="trash-alt"
                                                >
                                                    <path
                                                        fill={
                                                            props.theme ==
                                                            'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                        }
                                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </HoverTooltip>
                                    </div>
                                )}

                                <input
                                    className="flex p-1 min-w-40 h-8 items-center justify-start focus:outline-none text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818]"
                                    value={userFilter}
                                    onChange={(e) =>
                                        handleInputChange(e.target.value)
                                    }
                                ></input>
                            </div>
                            <div className="flex flex-col w-full h-fit items-center justify-center">
                                <div className="flex flex-row w-full gap-1 p-1 py-3 items-center justify-center rounded-t-md text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                    <div className="flex w-5 items-center justify-center "></div>
                                    <div className="flex w-10 items-center justify-center ">
                                        №
                                    </div>
                                    <div className="flex flex-1 items-center justify-center ">
                                        Фамилия Имя Отчество
                                    </div>
                                    <div className="flex w-48 items-center justify-center ">
                                        Подразделение
                                    </div>
                                    <div className="flex w-48 items-center justify-center ">
                                        Должность
                                    </div>
                                </div>
                                <div className="flex flex-col w-full h-[400px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                    {userFilteredList.map((item, index) => (
                                        <div
                                            key={item.guid}
                                            className="flex flex-col w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                            onClick={() => {
                                                setUserFilter([''])
                                                handleCheckboxChangeSupervisor(
                                                    String(item.guid)
                                                )
                                            }}
                                        >
                                            <div className="flex flex-row w-full gap-1 items-center justify-center text-[14px] ">
                                                <div className="flex w-5 items-center justify-center ">
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedSupervisor ==
                                                                '' ||
                                                            selectedSupervisor ==
                                                                null
                                                                ? false
                                                                : selectedSupervisor.includes(
                                                                      String(
                                                                          item.guid
                                                                      )
                                                                  )
                                                        }
                                                        onChange={() => {
                                                            if (
                                                                item?.guid !=
                                                                null
                                                            ) {
                                                                handleCheckboxChangeSupervisor(
                                                                    String(
                                                                        item.guid
                                                                    )
                                                                ) // Преобразование в строку
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                    {index + 1}
                                                </div>
                                                <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                                    {`${item.last_name ? item.last_name : ''} ${item.first_name ? item.first_name : ''} ${item.patronymic ? item.patronymic : ''}`}
                                                </div>
                                                <div className="flex w-48 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    {getNameStruct(
                                                        item?.struct_guid || ''
                                                    )}
                                                </div>
                                                <div className="flex w-48 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    {item.position}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-row text-right justify-end items-center gap-2">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleChangeSupervisorPopup}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={handleSupervisorChange}
                                >
                                    {isApprovalsAdd ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        'Изменить'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Section>
    )
}

export default ContractCardUserInfo
